/* The three scheduling reads and their writes. All `GET`/`PUT`, and none of them takes a key.
 *
 * **No `Idempotency-Key` anywhere in this file, and that is the API's shape rather than an
 * omission.** Every one of these resources has its natural key in the URL and its whole state in
 * the body, so a `PUT` is idempotent by construction — `shared/api/http.ts`'s `put` deliberately
 * takes no key parameter, and the platform's `post-idempotency-key` gate has nothing to enforce
 * here.
 *
 * Each mutation invalidates its own read **and** the `audit` namespace, because every write here
 * lands in the chain and the connection page renders its history from it — the defect §22 records
 * for the score listing, where a hand-listed invalidation set never reached the panel that had gone
 * stale.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  HarvestSchedule,
  HarvestScheduleWrite,
} from '@/shared/api/generated/catalog';
import { isHarvestSchedule } from '@/shared/api/generated/catalog';
import type {
  SchedulingSummary,
  ExecutionSchedule,
  ExecutionScheduleWrite,
  ExecutionTrigger,
  ExecutionTriggerWrite,
} from '@/shared/api/generated/rules';
import {
  isSchedulingSummaryList,
  isExecutionSchedule,
  isExecutionTrigger,
} from '@/shared/api/generated/rules';
import { getOne, put, remove } from '@/shared/api/http';

/* Keyed under the owning module's namespace rather than a `scheduling` one, so invalidating
   `['catalog']` after a harvest — which `useDeleteConnection` and friends already do — also
   refreshes a schedule card that may have just recorded an outcome. A separate namespace would put
   this cache outside every existing invalidation and make it the one panel that never updates. */
export const HARVEST_SCHEDULE_KEY = ['catalog', 'harvest-schedule'] as const;
export const EXECUTION_SCHEDULE_KEY = ['rules', 'execution-schedule'] as const;
export const EXECUTION_TRIGGER_KEY = ['rules', 'execution-trigger'] as const;

/* The batch read's namespace. Every write below invalidates it by **prefix**, so a schedule
   configured on one screen refreshes the bindings table on another without either knowing which
   page the other is showing. Listing the exact id sets would be the hand-listed invalidation this
   file's own header warns about, on a key whose set is not knowable from the mutation. */
export const BINDING_SCHEDULING_KEY = ['rules', 'binding-scheduling'] as const;

function harvestUrl(connectionId: string): string {
  return `/v1/catalog/connections/${encodeURIComponent(connectionId)}/harvest-schedule`;
}

function scheduleUrl(bindingId: string): string {
  return `/v1/rules/bindings/${encodeURIComponent(bindingId)}/execution-schedule`;
}

function triggerUrl(bindingId: string): string {
  return `/v1/rules/bindings/${encodeURIComponent(bindingId)}/execution-trigger`;
}

/* ------------------------------------------------------------------ harvest schedule */

/** One source's harvest schedule.
 *
 * **A 200 with `configured: false` is the ordinary answer, not an error**, so there is no branch
 * here for it — the component reads `configured`. That is the API's choice and the right one: a 404
 * would be indistinguishable from "no such connection", and the two send a reader to different
 * places.
 */
export function useHarvestSchedule(
  connectionId: string,
): UseQueryResult<HarvestSchedule> {
  return useQuery({
    queryKey: [...HARVEST_SCHEDULE_KEY, connectionId],
    queryFn: () =>
      getOne(harvestUrl(connectionId), isHarvestSchedule, 'harvest schedule'),
  });
}

export interface HarvestScheduleRequest {
  readonly connectionId: string;
  readonly body: HarvestScheduleWrite;
}

export function useConfigureHarvestSchedule(): UseMutationResult<
  HarvestSchedule,
  Error,
  HarvestScheduleRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, body }: HarvestScheduleRequest) =>
      put(harvestUrl(connectionId), isHarvestSchedule, 'harvest schedule', body),
    onSuccess: (_schedule, { connectionId }) => {
      void client.invalidateQueries({
        queryKey: [...HARVEST_SCHEDULE_KEY, connectionId],
      });
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export interface EnabledRequest {
  readonly id: string;
  readonly enabled: boolean;
}

/** Switch a stored harvest schedule on or off. **Its own route, and its own hook.**
 *
 * A client that had to `PUT` the whole schedule in order to disable it could disable it *and*
 * silently change the cadence in one request, and the audit chain would record one action. The API
 * splits them for that reason and this keeps the split.
 */
export function useSetHarvestScheduleEnabled(): UseMutationResult<
  HarvestSchedule,
  Error,
  EnabledRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: EnabledRequest) =>
      put(`${harvestUrl(id)}/enabled`, isHarvestSchedule, 'harvest schedule', {
        enabled,
      }),
    onSuccess: (_schedule, { id }) => {
      void client.invalidateQueries({ queryKey: [...HARVEST_SCHEDULE_KEY, id] });
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

/** Remove a source's harvest schedule, so it is harvested only when somebody asks.
 *
 * **No `Idempotency-Key`**: a DELETE cannot double, and a second one answers 404 because there is
 * then nothing to remove — the route's own reasoning, kept rather than restated.
 */
export function useRemoveHarvestSchedule(): UseMutationResult<void, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) => remove(harvestUrl(connectionId)),
    onSuccess: (_result, connectionId) => {
      void client.invalidateQueries({
        queryKey: [...HARVEST_SCHEDULE_KEY, connectionId],
      });
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

/* ------------------------------------------------------------------ execution schedule */

export function useExecutionSchedule(
  bindingId: string,
): UseQueryResult<ExecutionSchedule> {
  return useQuery({
    queryKey: [...EXECUTION_SCHEDULE_KEY, bindingId],
    queryFn: () =>
      getOne(scheduleUrl(bindingId), isExecutionSchedule, 'execution schedule'),
  });
}

export interface ExecutionScheduleRequest {
  readonly bindingId: string;
  readonly body: ExecutionScheduleWrite;
}

export function useConfigureExecutionSchedule(): UseMutationResult<
  ExecutionSchedule,
  Error,
  ExecutionScheduleRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ bindingId, body }: ExecutionScheduleRequest) =>
      put(scheduleUrl(bindingId), isExecutionSchedule, 'execution schedule', body),
    onSuccess: (_schedule, { bindingId }) => {
      void client.invalidateQueries({ queryKey: [...EXECUTION_SCHEDULE_KEY, bindingId] });
      void client.invalidateQueries({ queryKey: BINDING_SCHEDULING_KEY });
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export function useSetExecutionScheduleEnabled(): UseMutationResult<
  ExecutionSchedule,
  Error,
  EnabledRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: EnabledRequest) =>
      put(`${scheduleUrl(id)}/enabled`, isExecutionSchedule, 'execution schedule', {
        enabled,
      }),
    onSuccess: (_schedule, { id }) => {
      void client.invalidateQueries({ queryKey: [...EXECUTION_SCHEDULE_KEY, id] });
      void client.invalidateQueries({ queryKey: BINDING_SCHEDULING_KEY });
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

/* ------------------------------------------------------------------ harvest trigger */

export function useExecutionTrigger(bindingId: string): UseQueryResult<ExecutionTrigger> {
  return useQuery({
    queryKey: [...EXECUTION_TRIGGER_KEY, bindingId],
    queryFn: () => getOne(triggerUrl(bindingId), isExecutionTrigger, 'harvest trigger'),
  });
}

export interface ExecutionTriggerRequest {
  readonly bindingId: string;
  readonly body: ExecutionTriggerWrite;
}

export function useConfigureExecutionTrigger(): UseMutationResult<
  ExecutionTrigger,
  Error,
  ExecutionTriggerRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ bindingId, body }: ExecutionTriggerRequest) =>
      put(triggerUrl(bindingId), isExecutionTrigger, 'harvest trigger', body),
    onSuccess: (_trigger, { bindingId }) => {
      void client.invalidateQueries({ queryKey: [...EXECUTION_TRIGGER_KEY, bindingId] });
      void client.invalidateQueries({ queryKey: BINDING_SCHEDULING_KEY });
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export function useSetExecutionTriggerEnabled(): UseMutationResult<
  ExecutionTrigger,
  Error,
  EnabledRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: EnabledRequest) =>
      put(`${triggerUrl(id)}/enabled`, isExecutionTrigger, 'harvest trigger', {
        enabled,
      }),
    onSuccess: (_trigger, { id }) => {
      void client.invalidateQueries({ queryKey: [...EXECUTION_TRIGGER_KEY, id] });
      void client.invalidateQueries({ queryKey: BINDING_SCHEDULING_KEY });
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

/* ------------------------------------------------------ both answers, for a page of bindings */

/** Schedule and trigger for many bindings, in one request.
 *
 * **This exists because the per-binding hooks above brought the API down.** `SchedulingCell`
 * called both of them, and the bindings table renders one cell per row — so a page of eight
 * bindings opened sixteen concurrent requests, each holding a database session for its whole life,
 * against a connection pool that was running on its library default of fifteen. Every read past
 * the fifteenth waited the full checkout timeout and raised. The per-binding hooks are kept
 * because the rule's own page legitimately wants one binding; what changed is that a *list* no
 * longer asks one question per row.
 *
 * **Returns a Map rather than the array the API sends.** Every caller is rendering rows and needs
 * a lookup by id; building that per row from an array would be the quadratic scan the batching was
 * meant to remove, one layer up.
 *
 * **A missing entry means the read did not land, never "nothing is scheduled".** The route answers
 * with an entry per id it was given, including `configured: false`, so a gap here can only be a
 * failed or in-flight request — and `SchedulingCell` renders that as unknown rather than as an
 * absence, which is the conflation §9 records shipping on three screens.
 */
export function useBindingScheduling(
  bindingIds: readonly string[],
): UseQueryResult<ReadonlyMap<string, SchedulingSummary>> {
  /* Sorted and de-duplicated so the key is a function of the SET, not of the order React happened
     to render in. Without this, re-sorting the table by name would look like a different query and
     refetch the identical page. */
  const wanted = [...new Set(bindingIds)].sort();

  return useQuery({
    queryKey: [...BINDING_SCHEDULING_KEY, wanted.join(',')],
    /* The API refuses an empty read with a 422 — deliberately, because an empty answer is
       indistinguishable from "none of these are scheduled". So a table with no rows asks nothing
       rather than asking for nothing. */
    enabled: wanted.length > 0,
    queryFn: async () => {
      const list = await getOne(
        `/v1/rules/bindings/scheduling?${wanted
          .map((id) => `bindingIds=${encodeURIComponent(id)}`)
          .join('&')}`,
        isSchedulingSummaryList,
        'binding scheduling',
      );
      return new Map(list.items.map((item) => [item.binding_id, item]));
    },
  });
}
